import moment from "moment-timezone";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  BLANK,
  DAILY_PROFILE,
  EXISTING_PLAN_PROFILE,
  INVALID_TIME_FORMAT,
  TOTAL_DUTY_MUST_BE_AT_LEAST,
  AM_TIME_IN_DEPOT_CANT_BE_GREATER,
  PM_TIME_IN_DEPOT_CANT_BE_GREATER,
} from "../../../consts/strings";
import { palette } from "../../../theme/palette";
import { CreatePlanActions } from "./CreatePlanActions";
import { CreatePlanFirstStepContent } from "./CreatePlanFirstStepContent";
import { CreatePlanSecondStepContent } from "./CreatePlanSecondStepContent";
import { Modal } from "../Modal";
import { createPlan } from "../../../reducers/matrixProfile/thunkActions";
import {
  setCreateDatedPlanModalOpen,
  setCreateNewTemplateModalOpen,
} from "../../../reducers/ownApp";
import {
  depotDriverValuesSelector,
  partialPostcodesVersionsSelector,
} from "../../../reducers/depot/selectors";
import { isHHMMFormat } from "../../../utils/date";
import { Text, View } from "../../Base";

export const CreatePlan = ({ open, isDatedPlan }) => {
  const dispatch = useDispatch();
  const { minDutyTime, maxAmTimeInDepot, maxPmTimeInDepot } = useSelector(
    depotDriverValuesSelector
  );
  const partialPostcodesVersions = useSelector(
    partialPostcodesVersionsSelector
  );

  const [step, setStep] = useState(0);
  const [date, setDate] = useState();
  const [name, setName] = useState("");
  const [basedOn, setBasedOn] = useState("");
  const [selectedPlan, setSelectedPlan] = useState();
  const [driverSource, setDriverSource] = useState();
  const [selectedDay, setSelectedDay] = useState();
  const [dutyTime, setDutyTime] = useState();
  const [amTimeInDepot, setAmTimeInDepot] = useState();
  const [pmTimeInDepot, setPmTimeInDepot] = useState();
  const [isNameValid, setIsNameValid] = useState(false);
  const [errors, setErrors] = useState({});

  const partialPostcodesEffectiveDate =
    date &&
    partialPostcodesVersions.reduce((acc, { effectiveDate }) => {
      const formattedDate = moment(date)
        .tz("Europe/London")
        .format("YYYY-MM-DD");
      if (effectiveDate <= formattedDate && (!acc || acc < effectiveDate))
        return effectiveDate;
      else return acc;
    }, null);

  const nextDisabled =
    (isDatedPlan && !date) ||
    !isNameValid ||
    basedOn.length === 0 ||
    (basedOn !== BLANK && !selectedPlan);
  const hasErrors = !!Object.keys(errors).length;
  const optionalDisabled =
    nextDisabled ||
    (driverSource !== EXISTING_PLAN_PROFILE &&
      ![0, 1, 2, 3, 4, 5, 6].includes(selectedDay));
  const saveDisabled = optionalDisabled || hasErrors;
  const allowDriverFromPlan =
    basedOn !== BLANK && !!selectedPlan?.value?.driverTemplateId;

  useEffect(() => {
    setStep(0);
    setDate(null);
    setSelectedDay(null);
    setSelectedPlan(null);
    setBasedOn("");
    setName("");
    setDriverSource(null);
    setDutyTime(null);
    setAmTimeInDepot(null);
    setPmTimeInDepot(null);
    setIsNameValid(false);
    setErrors({});
  }, [open]);

  useEffect(() => {
    if (step) return;
    setSelectedDay(null);
    setDriverSource(null);
    setDutyTime(null);
    setAmTimeInDepot(null);
    setPmTimeInDepot(null);
    setErrors({});
  }, [step]);

  useEffect(() => {
    const errors = {};
    if (dutyTime) {
      const isValid = isHHMMFormat(dutyTime);
      if (!isValid) errors.dutyTime = INVALID_TIME_FORMAT;
      else if (dutyTime < minDutyTime)
        errors.dutyTime = TOTAL_DUTY_MUST_BE_AT_LEAST(minDutyTime);
    }

    if (amTimeInDepot) {
      const isValid = isHHMMFormat(amTimeInDepot);
      if (!isValid) errors.amTimeInDepot = INVALID_TIME_FORMAT;
      else if (amTimeInDepot > maxAmTimeInDepot)
        errors.amTimeInDepot =
          AM_TIME_IN_DEPOT_CANT_BE_GREATER(maxAmTimeInDepot);
    }

    if (pmTimeInDepot) {
      const isValid = isHHMMFormat(pmTimeInDepot);
      if (!isValid) errors.pmTimeInDepot = INVALID_TIME_FORMAT;
      else if (pmTimeInDepot > maxPmTimeInDepot)
        errors.pmTimeInDepot =
          PM_TIME_IN_DEPOT_CANT_BE_GREATER(maxPmTimeInDepot);
    }

    setErrors(errors);
  }, [
    dutyTime,
    amTimeInDepot,
    pmTimeInDepot,
    minDutyTime,
    maxAmTimeInDepot,
    maxPmTimeInDepot,
  ]);

  const handleClose = () => {
    dispatch(setCreateNewTemplateModalOpen(false));
    dispatch(setCreateDatedPlanModalOpen(false));
  };

  const onSavePlan = () =>
    dispatch(
      createPlan({
        date,
        name,
        basedOn,
        sourcePlan: selectedPlan,
        driverDay: selectedDay,
        dutyTime,
        amTimeInDepot,
        pmTimeInDepot,
      })
    );

  return (
    <Modal
      open={open}
      onClose={handleClose}
      title={isDatedPlan ? "Create dated plan" : "Create new template"}
      content={
        <>
          {!step ? (
            <CreatePlanFirstStepContent
              isDatedPlan={isDatedPlan}
              onDateChanged={setDate}
              onNameChanged={setName}
              onBasedOnChanged={(value) => {
                setBasedOn(value);
                setSelectedPlan();
              }}
              onSelectedPlanChanged={setSelectedPlan}
              date={date}
              name={name}
              basedOn={basedOn}
              selectedPlan={selectedPlan}
              setIsNameValid={setIsNameValid}
            />
          ) : (
            <CreatePlanSecondStepContent
              date={date}
              name={name}
              basedOn={basedOn}
              selectedPlan={selectedPlan}
              allowDriverFromPlan={allowDriverFromPlan}
              driverSource={driverSource}
              onDriverSourceChange={(driverSource) => {
                if (driverSource !== DAILY_PROFILE) setSelectedDay(null);
                setDriverSource(driverSource);
              }}
              selectedDay={selectedDay}
              setSelectedDay={setSelectedDay}
              optionalDisabled={optionalDisabled}
              dutyTime={dutyTime}
              onDutyTimeChange={setDutyTime}
              amTimeInDepot={amTimeInDepot}
              onAmTimeInDepotChange={setAmTimeInDepot}
              pmTimeInDepot={pmTimeInDepot}
              onPmTimeInDepotChange={setPmTimeInDepot}
              errors={errors}
              setErrors={setErrors}
            />
          )}
          {partialPostcodesEffectiveDate ? (
            <View style={{ width: "20rem" }}>
              <Text
                style={{
                  color: palette.dpdRed,
                  fontSize: "14px",
                  margin: "10px 0px",
                }}
              >
                <b>Warning:</b> This dated plan will use the future postcode
                allocation (effective from
                <b>
                  {" "}
                  {moment(partialPostcodesEffectiveDate)
                    .tz("Europe/London")
                    .format("DD/MM/YYYY")}
                </b>
                )
              </Text>
            </View>
          ) : null}
        </>
      }
      actions={
        <CreatePlanActions
          step={step}
          setStep={setStep}
          nextDisabled={nextDisabled}
          saveDisabled={saveDisabled}
          onSave={onSavePlan}
        />
      }
    ></Modal>
  );
};
